import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Edit2, Trash2, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { message } from 'antd';
import aarogyaLogo from '../assets/Aarogya india hindia updated logo.svg';
import addBillProfileIcon from '../assets/Add bill profile.svg';
import { companyProfileApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { ASSET_BASE_URL as ASSET_URL } from '../services/api';

function BillingSelfProfile() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [togglingId, setTogglingId] = useState(null); // track which row is toggling

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const data = await companyProfileApi.getAllProfiles();
      console.log("getAllProfiles",data)
      setProfiles(data || []);
      if (data && data.length > 0) {
        setSelectedProfile(data[0]);
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile) => {
    navigate('/billing/add-profile', {
      state: { profile, isEdit: true }
    });
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;
    try {
      await companyProfileApi.deleteProfile(profileId);
      const updated = profiles.filter(p => p.id !== profileId);
      setProfiles(updated);
      if (selectedProfile?.id === profileId) {
        setSelectedProfile(updated.length > 0 ? updated[0] : null);
      }
      message.success('Profile deleted successfully');
    } catch (err) {
      console.error('Error deleting profile:', err);
      message.error('Failed to delete profile');
    }
  };

  // ✅ Toggle active/inactive status
  const handleToggleStatus = async (e, profile) => {
    e.stopPropagation();
    try {
      setTogglingId(profile.id);
      await companyProfileApi.setactive(profile.id);
      console.log("profile.id",profile.id)
      // Update local state — flip the status
      const updated = profiles.map(p =>
        p.id === profile.id ? { ...p, status: !p.status, active: !p.active } : p
      );
      setProfiles(updated);

      // Update selectedProfile if it's the same one
      if (selectedProfile?.id === profile.id) {
        setSelectedProfile(prev => ({ ...prev, status: !prev.status, active: !prev.active }));
      }

      const isNowActive = !profile.status && !profile.active;
      message.success(`Profile ${isNowActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Toggle status failed:', err);
      message.error('Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const isActive = (profile) => {
    // Backend may return status as boolean true/false or string 'ACTIVE'/'INACTIVE'
    if (typeof profile.status === 'boolean') return profile.status;
    if (typeof profile.active === 'boolean') return profile.active;
    if (typeof profile.status === 'string') return profile.status === 'ACTIVE' || profile.status === 'true';
    return true; // default assume active
  };

  return (
    <div className={`space-y-6 transition-colors duration-300 ${isDark ? 'min-h-screen' : ''}`}>

      {/* ── Page Title ── */}
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Self profile</h1>
        <div className="flex items-center space-x-3">
          <button className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <Filter className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
          <button
            onClick={() => navigate('/billing/add-profile')}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
          >
            <img src={addBillProfileIcon} alt="Add Bill Profile" className="w-9 h-9" />
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="flex flex-col lg:flex-row gap-6">

        {/* ── Table ── */}
        <div className={`flex-1 rounded-xl border shadow-md overflow-hidden transition-all ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: currentTheme.color }} />
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Loading profiles...</p>
            </div>
          ) : profiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No profiles found</p>
              <button
                onClick={() => navigate('/billing/add-profile')}
                className="px-4 py-2 text-white rounded-lg"
                style={{ backgroundColor: currentTheme.color }}
              >
                Add Profile
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[1500px] w-full">
                <thead>
                  <tr className="text-white" style={{ background: currentTheme.color }}>
                    {[
                      'COMPANY NAME', 'NAME', 'MOBILE', 'EMAIL',
                      'PAN NUMBER', 'GST NUMBER', 'FSSAI NUMBER',
                      'ADDRESS', 'STATUS', 'ACTION'
                    ].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'divide-y divide-white/5' : 'divide-y divide-gray-200'}`}>
                  {profiles.map((profile) => (
                    <tr
                      key={profile.id}
                      className="cursor-pointer transition-colors"
                      style={{
                        backgroundColor: selectedProfile?.id === profile.id
                          ? `${currentTheme.color}15` : undefined,
                        borderLeft: selectedProfile?.id === profile.id
                          ? `4px solid ${currentTheme.color}` : '4px solid transparent',
                      }}
                      onClick={() => setSelectedProfile(profile)}
                    >
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.companyName || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.name || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.contactNumber || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.email || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.panNumber || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.gstNumber || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{profile.fssaiNumber || 'N/A'}</td>
                      <td className={`px-4 py-3 text-sm max-w-[200px] ${isDark ? 'text-gray-400' : 'text-gray-900'}`}>
                        <span className="line-clamp-2">{profile.locality || 'N/A'}</span>
                      </td>

                      {/* ✅ Status column with toggle */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* Status badge */}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                            isActive(profile)
                              ? (isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-600 border-green-200')
                              : (isDark ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-500 border-red-200')
                          }`}>
                            {isActive(profile) ? 'Active' : 'Inactive'}
                          </span>

                          <button
                            onClick={(e) => handleToggleStatus(e, profile)}
                            disabled={togglingId === profile.id}
                            className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-all duration-300 ease-in-out focus:outline-none border-2"
                            style={{
                              backgroundColor: isActive(profile) ? currentTheme.color : '#ffffff',
                              borderColor: isDark ? '#ffffff' : '#D1D5DB',
                            }}
                            title={isActive(profile) ? 'Deactivate' : 'Activate'}
                          >
                            <span
                              className={`pointer-events-none absolute top-[2px] left-[2px] h-4 w-4 rounded-full shadow-md transition-all duration-300 ease-in-out transform border-2 border-white ${
                                isActive(profile) ? 'translate-x-5 bg-white' : 'translate-x-0 bg-gray-400'
                              }`}
                            />
                          </button>
                        </div>
                      </td>

                      {/* Action buttons */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(profile); }}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-orange-50'}`}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-orange-500" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(profile.id); }}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-red-50'}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Right Panel: Attachments ── */}
        {selectedProfile && (
          <div className={`w-full lg:w-[380px] rounded-xl border shadow-md p-6 flex-shrink-0 transition-all ${isDark ? 'bg-[#1e293b] border-white/10 text-white' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Attachment</h2>
              <button
                onClick={() => handleEdit(selectedProfile)}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                style={{ color: currentTheme.color }}
                title="Edit profile"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">

              {/* Company Logo */}
              <div>
                <label className={`text-sm font-bold block mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Company Logo</label>
                <div className={`rounded-lg p-4 border flex items-center justify-center min-h-[64px] transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  {selectedProfile.companyLogoUrl ? (
                    <img
                      src={selectedProfile.companyLogoUrl.startsWith('http')
                        ? selectedProfile.companyLogoUrl
                        : `${ASSET_URL}/uploads/${selectedProfile.companyLogoUrl}`}
                      alt="Company Logo"
                      className="h-12 w-auto object-contain"
                      onError={e => { e.target.src = aarogyaLogo; }}
                    />
                  ) : (
                    <img src={aarogyaLogo} alt="Default Logo" className="h-12 w-auto object-contain" />
                  )}
                </div>
              </div>

              {/* Tag Line */}
              <div>
                <label className={`text-sm font-bold block mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Tag line</label>
                <div className={`rounded-lg px-4 py-3 border transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{selectedProfile.tagLine || 'N/A'}</p>
                </div>
              </div>

              {/* Signature */}
              <div>
                <label className={`text-sm font-bold block mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Signature</label>
                <div className={`rounded-lg p-4 border flex items-center justify-center min-h-[88px] transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  {selectedProfile.signatureUrl ? (
                    <img
                      src={selectedProfile.signatureUrl.startsWith('http')
                        ? selectedProfile.signatureUrl
                        : `${ASSET_URL}/uploads/${selectedProfile.signatureUrl}`}
                      alt="Signature"
                      className="h-16 w-auto object-contain"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No signature uploaded</span>
                  )}
                </div>
              </div>

              {/* UPI Scanner */}
              <div>
                <label className={`text-sm font-bold block mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>UPI Scanner</label>
                <div className={`rounded-lg p-4 border flex items-center justify-center min-h-[140px] transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  {selectedProfile.upiScannerUrl ? (
                    <img
                      src={selectedProfile.upiScannerUrl.startsWith('http')
                        ? selectedProfile.upiScannerUrl
                        : `${ASSET_URL}/uploads/${selectedProfile.upiScannerUrl}`}
                      alt="UPI QR Code"
                      className="h-28 w-28 object-contain rounded"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No UPI scanner uploaded</span>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillingSelfProfile;